/* eslint-disable max-len */
import { CircularProgress } from "@mui/material";
import { checkTransactionConfirmationReceipt } from "../../utils/methods";
import { useState } from "react";
import { getContract, getMetaMaskSigner, ecoFuturesTokenContractAddress } from "../../services/contract.service";
import { LandToken } from "../../types/LandToken";
import { useNavigate } from "react-router";
import { AppRoutes } from "../../config";
import LandActionPopup, { PopupCTA, PopupFootnote } from "../LandPage/LandActionPopup";

type OfferCreationModalProps = {
  token: LandToken;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
};

const OfferCreationModal = ({ token, open, setOpen, onSuccess }: OfferCreationModalProps) => {
  const navigate = useNavigate();
  const [message, setMessage] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [processingState, setProcessingState] = useState<"idle" | "processing" | "success">("idle");

  const handleCloseModal = () => {
    setOpen(false);
    setProcessingState("idle");
    setLoading(false);
    setMessage("");
  };

  const handleCancelProcess = () => {
    setLoading(false);
    setProcessingState("idle");
    setMessage("Process cancelled by user");
  };

  // Build bidding tiers with active flag based on activated easement level
  // const getHighestActiveTier = useCallback(() => {
  //   if (!token?.meta) return null;

  //   const activatedLevel = token?.etNft?.easementActivated?.level;
  //   const sortedEasements = [...(token?.easements || [])].sort(
  //     (a, b) => Number(a?.level ?? Number.MAX_SAFE_INTEGER) - Number(b?.level ?? Number.MAX_SAFE_INTEGER)
  //   );

  //   const biddingTiers = [
  //     {
  //       title: "Tier 1",
  //       price: token.meta.startingBid,
  //       active: activatedLevel !== undefined && activatedLevel >= 0,
  //     },
  //     ...sortedEasements.map((easement) => ({
  //       title: `Tier ${easement.level + 1}`,
  //       price: easement.activationBid,
  //       active: activatedLevel !== undefined && activatedLevel >= easement.level,
  //     })),
  //   ];

  //   const activeTiers = biddingTiers.filter((tier) => tier.active);
  //   if (activeTiers.length === 0) return null;

  //   return activeTiers.reduce((prev, current) => {
  //     const prevPrice = typeof prev.price === "number" ? prev.price : Number(prev.price || 0);
  //     const currentPrice = typeof current.price === "number" ? current.price : Number(current.price || 0);
  //     return prevPrice > currentPrice ? prev : current;
  //   });
  // }, [token]);

  // Calculate mint capacity
  // const getMintCapacity = useCallback(() => {
  //   if (!token?.etNft?.totalTreeForEasement || !token?.etNft?.easementActivated?.rewardClaimed) {
  //     return "N/A";
  //   }

  //   const total = formatValue(token.etNft.totalTreeForEasement, false);
  //   const claimed = formatValue(token.etNft.easementActivated.rewardClaimed, false);
  //   const remaining = total - claimed;

  //   return `${sixDigits(formatValue(remaining, true, 3))} TR3`;
  // }, [token]);

  // Get last sold price
  // const getLastSoldPrice = useCallback(() => {
  //   if (!token?.etNft?.etAuction?.endingPrice) {
  //     return "N/A";
  //   }
  //   return `$${formatValue(token.etNft.etAuction.endingPrice, true, 2)}`;
  // }, [token]);

  const handleOfferCreation = async () => {
    setLoading(true);
    setProcessingState("processing");
    try {
      if (token && token.meta && window.ethereum) {
        setMessage("Requesting approval...");
        const signer = await getMetaMaskSigner();
        const NFTContract = getContract("ecofutures-token", signer);
        const tokenId = Number(token?.etNft?.id);

        if (NFTContract && tokenId) {
          setMessage("Approving NFT for offers...");
          const awaitNFTApproval = await NFTContract.approve(ecoFuturesTokenContractAddress, tokenId);
          const r = await checkTransactionConfirmationReceipt(awaitNFTApproval.hash);
          const receipt = r as any;

          if (receipt?.status !== "0") {
            setMessage("Opening NFT for offers...");
            const openNFT = await NFTContract.openNFTForOffers(tokenId);
            const r2 = await checkTransactionConfirmationReceipt(openNFT.hash);
            const receipt2 = r2 as any;

            if (receipt2?.status !== "0") {
              setMessage("NFT successfully listed for offers!");
              setProcessingState("success");

              // Wait a moment to show success message
              setTimeout(async () => {
                if (onSuccess) {
                  onSuccess();
                  handleCloseModal();
                } else {
                  navigate(AppRoutes.Home.path);
                }
              }, 500);
            } else {
              setMessage("Failed to open NFT for offers");
              setProcessingState("idle");
            }
          } else {
            setMessage("NFT approval failed");
            setProcessingState("idle");
          }
        }
      }
    } catch (error: any) {
      console.error("Error creating offer:", error);
      setMessage(error.reason || error.message || "Failed to create offer");
      setProcessingState("idle");
    } finally {
      if (processingState !== "success") {
        setTimeout(() => {
          setLoading(false);
        }, 2000);
      }
    }
  };

  // const highestActiveTier = getHighestActiveTier();

  // List-to-offer, on the land-card + purple-panel shell the offer / bid popups use (Figma
  // node 3002:4045). The rows are what the old frosted panel listed — the highest activated
  // tier, mint capacity, the contract countdown — with LAST SOLD FOR as the headline figure,
  // the same rows-then-highlight order that panel drew. The shell owns the mobile sheet /
  // desktop dialog split; this supplies only the panel's body.
  return (
    <LandActionPopup
      open={open}
      onClose={handleCloseModal}
      token={token}
      // statLabel="LAST SOLD FOR"
      // statValue={getLastSoldPrice()}
      // rows={[
      //   {
      //     label: highestActiveTier ? highestActiveTier.title : "Tier",
      //     value: highestActiveTier ? `$${formatValue(highestActiveTier.price, true, 2)}` : "0",
      //   },
      //   { label: "Mint capacity", value: getMintCapacity() },
      //   {
      //     label: "Contract period",
      //     value:
      //       token?.etNft?.easementActivated?.contractStart && token?.meta?.contractPeriod ? (
      //         <CountdownTimer
      //           medium
      //           sx={popupTimerSx}
      //           targetDate={moment(token.etNft.easementActivated.contractStart)
      //             .add(token.meta.contractPeriod, TERM_UNIT)
      //             .toDate()}
      //         />
      //       ) : (
      //         "N/A"
      //       ),
      //   },
      // ]}
    >
      {/* While the two approvals run the CTA becomes the cancel: same spinner-only face the
          old MintCancelButton had, and a tap still calls handleCancelProcess. */}
      {processingState === "processing" ? (
        <PopupCTA onClick={handleCancelProcess} variant="contained">
          <CircularProgress size={20} sx={{ color: "white" }} />
        </PopupCTA>
      ) : (
        <PopupCTA onClick={handleOfferCreation} disabled={loading} variant="contained">
          {loading ? "Loading..." : "List to offer"}
        </PopupCTA>
      )}

      {/* The transaction narration ("Approving NFT for offers…") and any revert reason. */}
      {message && <PopupFootnote>{message}</PopupFootnote>}
    </LandActionPopup>
  );
};

export default OfferCreationModal;
